/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { DownloadMultiScope } from '../../types/download';
import G17Client, { RequesterReportDataParams } from '../../services/G17Client';
import { handleRouteError, loggerMessage } from '../../logger';
import { getRenderedReport, getSDG, SupportedReportCodes } from '@features/report-output/outputs';
import { RequesterType } from '../../types/dataShare';

export interface DownLoadCardItem {
  code: string;
  name: string;
  isVisible?: boolean;
  isDisabled?: boolean;
  src?: string;
}

interface ShareDownloadCards {
  isCTL: boolean;
  fullAccess: boolean;
}

export const getShareDownloadCards = ({ isCTL, fullAccess }: ShareDownloadCards): DownLoadCardItem[] => [
  {
    code: 'gri',
    name: 'GRI aligned report',
    isVisible: !isCTL
  },
  {
    code: 'ungc',
    name: 'UNGC aligned report',
    isVisible: !isCTL
  },
  {
    code: 'tcfd',
    name: 'TCFD V1 aligned report',
    isVisible: true,
  },
  {
    code: 'tcfd_standard',
    name: 'TCFD V2 aligned report',
    isVisible: true,
  },
  {
    code: 'sasb',
    name: 'SASB aligned report',
    isVisible: !isCTL
  },
  {
    code: 'sdg',
    name: 'SDG aligned report',
    isVisible: true,
    isDisabled: !fullAccess,
  },
]

interface DataShareMultiScopeReport extends RequesterReportDataParams {
  requesterType: RequesterType,
  code: SupportedReportCodes;
  downloadScope: DownloadMultiScope;
}

export const getDataShareMultiScopeReport = async (dataShareMultiScopeReport: DataShareMultiScopeReport) => {

  const { requesterId, requesterType, initiativeId, surveyId, code, downloadScope } = dataShareMultiScopeReport;
  if (code === 'sdg') {
    return G17Client.requesterReportDataSDG({ requesterId, requesterType, initiativeId, surveyId })
      .then(({ sdgReport, survey }) => getSDG(survey, sdgReport))
      .catch(e => {
        handleRouteError(e, {
          debugMessage: 'Failed to generate SDG report',
          code,
          surveyId,
        })
        throw new Error('Unable to generate report SDG report')
      })
  }

  const generateReportDoc = getRenderedReport(code);
  if (!generateReportDoc) {
    loggerMessage(`Not supported "${code}" multi scope report`, {
      code,
      surveyId,
    })
    throw new Error(`Not supported report type ${code}`)
  }

  return G17Client.requesterReportData({
    requesterId,
    requesterType,
    initiativeId,
    surveyId,
    downloadScope
  }).then(({ survey, reportData }) => generateReportDoc(survey, reportData, downloadScope))
    .catch(e => {
      handleRouteError(e, {
        debugMessage: `Failed to generate multi scope data share report for "${code}"`,
        code,
        surveyId,
      })
      throw new Error('Unable to generate report')
    });
};
